import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { firstValueFrom } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { TranslateModule } from '@ngx-translate/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';

import type { SelectedEntity } from '@common/types/layout.type';
import { ESelectedEntityType } from '@common/types/layout.type';

import type { NormalizedBlockEntity, NormalizedColumnEntity, NormalizedSectionEntity } from '@web/types';

import { ProductRestrictionFacade } from '@web/core/services/product-restriction.facade';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';

import { NavigationColumnItemComponent } from '../navigation-column-item';

import { NavigationDrawerService } from '../navigation-drawer.service';

@Component({
    selector: 'web-navigation-specific-section-item',
    templateUrl: './navigation-specific-section-item.component.html',
    styleUrls: ['./navigation-specific-section-item.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BsDropdownModule, NavigationColumnItemComponent, TranslateModule],
})
export class NavigationSpecificSectionItemComponent {
    public readonly parentEntity = input.required<NormalizedBlockEntity>();
    public readonly parentIndex = input.required<number>();
    public readonly entity = input.required<NormalizedSectionEntity>();
    public readonly index = input.required<number>();
    public readonly selectedEntity = input.required<SelectedEntity>();

    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly sectionsFacade: SectionsFacade = inject(SectionsFacade);
    private readonly columnsFacade: ColumnsFacade = inject(ColumnsFacade);
    private readonly productRestrictionFacade: ProductRestrictionFacade = inject(ProductRestrictionFacade);
    private readonly navDrawerService: NavigationDrawerService = inject(NavigationDrawerService);

    private readonly entity$ = toObservable(this.entity);
    private readonly columnsEntities = toSignal(this.columnsFacade.columnsEntities$, { initialValue: {} });

    /** Whether this section's children are expanded — derived from the drawer service's expanded set. */
    public readonly showChildren = computed(() => this.navDrawerService.hasRowId(this.entity().id));

    public readonly isActive = computed(() => this.selectedEntity()?.entityId === this.entity().id);

    /** The last remaining section of a header/footer block cannot be deleted. */
    public readonly isDeleteDisabled = computed(() => this.parentEntity().children.length === 1);

    public readonly displayId = computed(() => this.entity().id);

    public readonly columns = computed(() => {
        const columns = this.columnsEntities();
        return this.entity().children.map((columnId) => columns[columnId] as NormalizedColumnEntity);
    });

    public readonly isSectionRestricted = toSignal(
        this.entity$.pipe(switchMap((entity) => this.productRestrictionFacade.sectionRestricted$(entity))),
        { initialValue: false }
    );

    public selectEntity(): void {
        if (this.navDrawerService.hasRowId(this.entity().id)) {
            this.navDrawerService.removeRowId(this.entity().id);
        } else {
            this.navDrawerService.addRowId(this.entity().id);
        }

        if (this.isActive()) {
            return;
        }

        this.layoutFacade.setSelectedEntityWithScroll({
            entityType: ESelectedEntityType.specificSection,
            entityId: this.entity().id,
        });
    }

    public async removeSection(): Promise<void> {
        if (this.isDeleteDisabled() || this.isSectionRestricted()) {
            return;
        }

        const section = await firstValueFrom(this.sectionsFacade.denormalizedSectionById$(this.entity().id));

        if (!section) {
            console.error('Section not found');
            return;
        }

        if (this.isActive()) {
            this.layoutFacade.unsetEntity();
        }

        this.sectionsFacade.removeSection(section, this.index());
    }
}
